import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useGetInfoApi } from '@/api/notice';
import mainBanner from '@/assets/img/banner/banner_main01.jpg';
import mainBanner2 from '@/assets/img/banner/banner_main02.jpg';
import mainBanner3 from '@/assets/img/banner/banner_main03.jpg';
import mainBanner4 from '@/assets/img/banner/banner_main04.jpg';
import ArrowRightIcon from '@/assets/img/icon/arrowRightIcon.svg';
import ContactIcon from '@/assets/img/icon/contactIcon.svg';
import GotoIcon from '@/assets/img/icon/gotoIcon.svg';
import InformationIcon from '@/assets/img/icon/informationIcon.svg';
import IntroduceIcon from '@/assets/img/icon/introduceIcon.svg';
import PublicationIcon from '@/assets/img/icon/publicationIcon.svg';
import RegisterIcon from '@/assets/img/icon/registerIcon.svg';
import SocietyIcon from '@/assets/img/icon/societyIcon.svg';
import logo1 from '@/assets/img/logo/logo1.svg';
import logo2 from '@/assets/img/logo/logo2.png';
import logo3 from '@/assets/img/logo/logo3.svg';
import logo4 from '@/assets/img/logo/logo4.png';
import logo5 from '@/assets/img/logo/logo5.png';
import logo6 from '@/assets/img/logo/logo6.png';
import logo7 from '@/assets/img/logo/logo7.png';
import logo8 from '@/assets/img/logo/logo8.png';
import logo9 from '@/assets/img/logo/logo9.png';
import { CardContainer } from '@/components/common/Card/CardContainer/style';
import CardNotice from '@/components/common/Card/CardNotice/CardNotice';
import SponsorLogoSection from '@/components/common/SponsorLogoSection/SponsorLogoSection';
import useIsMobile from '@/hooks/useIsMobile';
import * as S from '@/pages/Home/style';
import { convertInfoValue } from '@/utils/convertValue';

const logos = [
	{ logo: logo1, href: 'https://www.clean.go.kr/index.es?sid=a1' },
	{ logo: logo4, href: 'https://kcia.or.kr' },
	{ logo: logo5, href: 'https://www.kcii.re.kr' },
	{ logo: logo6, href: 'https://ifscc2025.com/' },
	{ logo: logo7, href: 'https://www.kofst.or.kr/' },
	{ logo: logo8, href: 'http://www.ifscc2026.com', padding: true },
	{ logo: logo9, href: 'https://ifscc.org', division: 'width' },
	{ logo: logo2, href: 'https://www.mfds.go.kr', padding: true },
	{ logo: logo3, href: 'https://www.nts.go.kr', padding: true },
];
const fixedCompanyLogos = logos.filter((item) => [logo1, logo2, logo3].includes(item.logo));
const slidingCompanyLogos = logos.filter((item) => ![logo1, logo2, logo3].includes(item.logo));

const heroBannerSlides = [
	{ src: mainBanner, alt: '메인 비주얼 배너 1' },
	{ src: mainBanner2, alt: '메인 비주얼 배너 2' },
	{ src: mainBanner3, alt: '메인 비주얼 배너 3' },
	{ src: mainBanner4, alt: '메인 비주얼 배너 4' },
];

const Home = () => {
	const navigate = useNavigate();
	const isMobile = useIsMobile(1100);
	const [activeTab, setActiveTab] = useState('공지사항');
	const { data: infoData } = useGetInfoApi({
		page: 1,
		type: convertInfoValue(activeTab),
		row: 5,
	});
	const getDynamicField = (g: any, field: string) => {
		const type = convertInfoValue(activeTab);
		return g[`${type}_${field}`] || ''; // 동적으로 필드 처리
	};
	return (
		<S.MainWrapper>
			<div className="container">
				<S.MainBanner>
					<Swiper
						className="hero-image-swiper"
						modules={[EffectFade, Autoplay]}
						effect="fade"
						fadeEffect={{ crossFade: true }}
						slidesPerView={1}
						speed={1100}
						spaceBetween={0}
						loop={true}
						loopAdditionalSlides={2}
						watchSlidesProgress
						autoplay={{
							delay: 5200,
							disableOnInteraction: false,
							stopOnLastSlide: false,
							waitForTransition: true,
							pauseOnMouseEnter: false,
						}}
					>
						{heroBannerSlides.map((slide, index) => (
							<SwiperSlide key={index}>
								<div className="img_container first">
									<img src={slide.src} alt={slide.alt} className="background" />
									<div className="overlay" />
								</div>
							</SwiperSlide>
						))}
					</Swiper>
					<div className="textBoxWrapper">
						<div className="textBox">
							<h1 className="title">
								THE SOCIETY OF COSMETIC
								<br />
								SCIENTISTS OF KOREA
							</h1>
							<p className="description">
								대한화장품학회가 더 큰 비전과 목표를 가지고
								<br style={isMobile ? {} : { display: 'none' }} /> 새로운 도전을 시작합니다.
								<br />
								(사)대한화장품학회 회원여러분과 함께, 화장품 산업의 미래를 이끌어 나가는데 <br style={isMobile ? {} : { display: 'none' }} />
								최선을 다하겠습니다.
							</p>
						</div>
					</div>
				</S.MainBanner>
			</div>
			<S.Description>
				<div className="main_description_title">
					<h2 className="description_title">
						대한화장품학회, <br style={isMobile ? {} : { display: 'none' }} />
						연구와 교류의 중심
					</h2>
					<p className="description_sub_title">
						화장품 산업의 미래를 이을 대한화장품학회에서 최신 연구를 접하고
						<br style={isMobile ? {} : { display: 'none' }} /> 전문가들과 소통하세요.
					</p>
				</div>
				<S.CardList>
					<CardContainer className={`border-right border-bottom`} onClick={() => navigate('/introduce')}>
						<S.CardWrapper>
							<S.CardTitle>
								학회소개
								<img src={ArrowRightIcon} alt="arrowRight" />
							</S.CardTitle>
							<S.CardSubTitle>
								화장품 연구 발전과 학술 교류를 위한 학회의
								<br style={isMobile ? { display: 'none' } : {}} />
								역사와 비전을 소개합니다.
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={IntroduceIcon} alt="introduce" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer className={`border-right border-bottom`} onClick={() => navigate('/auth/signup')}>
						<S.CardWrapper>
							<S.CardTitle>
								학회활동
								<img src={ArrowRightIcon} alt="arrowRight" />
							</S.CardTitle>
							<S.CardSubTitle>
								학회활동을 통해 최신 연구 동향을
								<br style={isMobile ? { display: 'none' } : {}} />
								공유하고 연구자들과 교류하세요.
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={SocietyIcon} alt="society" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer
						className={` ${isMobile ? 'border-right' : ''} border-bottom`}
						onClick={() => navigate('/society', { state: { menu: '학술행사 안내', depthMenu: '등록안내' } })}
					>
						<S.CardWrapper>
							<S.CardTitle>
								회원안내
								<img src={ArrowRightIcon} alt="arrowRight" />
							</S.CardTitle>
							<S.CardSubTitle>
								학술대회 참여, 연구 자료 이용, 전문가
								<br style={isMobile ? { display: 'none' } : {}} />
								네트워크 등의 혜택을 제공합니다.
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={RegisterIcon} alt="register" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer className={` ${isMobile ? '' : 'border-right'}`} onClick={() => navigate('/publication')}>
						<S.CardWrapper>
							<S.CardTitle>
								간행물
								<img src={ArrowRightIcon} alt="arrowRight" />
							</S.CardTitle>
							<S.CardSubTitle>
								회원들의 많은 훌륭한 논문의 게재된 학술지
								<br style={isMobile ? { display: 'none' } : {}} />를 확인해보세요.
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={PublicationIcon} alt="publication" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer className={` ${isMobile ? 'border-right' : ''}`} onClick={() => navigate('/information')}>
						<S.CardWrapper>
							<S.CardTitle>
								자료실
								<img src={ArrowRightIcon} alt="arrowRight" />
							</S.CardTitle>
							<S.CardSubTitle>
								최신 연구 자료, 학술 논문, 뉴스 및 학회 소식
								<br style={isMobile ? { display: 'none' } : {}} /> 등 다양한 정보를 확인할 수 있습니다.
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={InformationIcon} alt="introduce" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer className="dark" onClick={() => navigate('/contact')}>
						<S.CardWrapper>
							<S.CardTitle>
								문의하기
								<img src={ArrowRightIcon} alt="arrowRight" />
							</S.CardTitle>
							<S.CardSubTitle>
								대한화장품학회에 대한 질문과 기타 문<br style={isMobile ? { display: 'none' } : {}} />
								의사항에 대한 답변을 받을수 있습니다.
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={ContactIcon} alt="contact" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
				</S.CardList>
			</S.Description>
			<S.Description>
				<SponsorLogoSection />
			</S.Description>
			<S.Notice>
				<S.NoticeContent>
					<S.NoticeTabList>
						<S.Wrapper>
							<S.ButtonList>
								<S.TabButton isActive={activeTab === '공지사항'} onClick={() => setActiveTab('공지사항')}>
									공지사항
								</S.TabButton>
								<S.Divider />
								<S.TabButton isActive={activeTab === '자유게시판'} onClick={() => setActiveTab('자유게시판')}>
									자유게시판
								</S.TabButton>
							</S.ButtonList>
							<S.GotoButton type="button" onClick={() => navigate('/information', { state: { menu: activeTab } })}>
								바로가기
								<img src={GotoIcon} />
							</S.GotoButton>
						</S.Wrapper>
					</S.NoticeTabList>
					<S.NoticeContentList>
						<CardNotice>
							{infoData && (infoData[convertInfoValue(activeTab)] ?? []).length >= 1 && (
								<S.NoticeList>
									{(infoData[convertInfoValue(activeTab)] ?? []).map((g, i) => (
										<S.NoticeListItem key={i} onClick={() => navigate(`/information/notice/${getDynamicField(g, 'pk')}`, { state: { menu: activeTab } })}>
											<div className="title">{getDynamicField(g, 'title')}</div>
											<div className="sub_title">{dayjs(getDynamicField(g, 'enrollment_date')).format('YYYY-MM-DD')}</div>
										</S.NoticeListItem>
									))}
								</S.NoticeList>
							)}
							{(!infoData || (infoData[convertInfoValue(activeTab)] ?? [])?.length < 1) && (
								<S.NoticeEmptyBox>
									<S.NoData>해당 데이터가 없습니다.</S.NoData>
								</S.NoticeEmptyBox>
							)}
						</CardNotice>
					</S.NoticeContentList>
				</S.NoticeContent>
			</S.Notice>
			<S.Company>
				<S.CompanyFixedRow>
					{fixedCompanyLogos.map((logo, index) => (
						<S.CompanyCard key={index} onClick={() => window.open(logo.href)} padding={logo.padding} division={logo.division as 'width' | 'height' | undefined}>
							<img src={logo.logo} alt="introduce" />
						</S.CompanyCard>
					))}
				</S.CompanyFixedRow>
				<S.CompanySlider>
					<Swiper
						modules={[Navigation, Pagination, Autoplay]}
						slidesPerView={'auto'}
						spaceBetween={16}
						navigation
						pagination={{
							clickable: true,
							el: '.swiper-pagination-custom',
						}}
						autoplay={{
							delay: 4000,
							disableOnInteraction: false,
						}}
						loop={true}
						style={{ width: '100%', height: '100%' }}
					>
						{slidingCompanyLogos.map((logo, index) => (
							<SwiperSlide className="company_item" key={index}>
								<S.CompanyCard onClick={() => window.open(logo.href)} padding={logo.padding} division={logo.division as 'width' | 'height' | undefined}>
									<img src={logo.logo} alt="introduce" />
								</S.CompanyCard>
							</SwiperSlide>
						))}
					</Swiper>
				</S.CompanySlider>
			</S.Company>
		</S.MainWrapper>
	);
};

export default Home;
